import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'

import FeaturedTable from '../FeaturedTable/FeaturedTable'
import PaginationFooter from '../PaginationFooter/PaginationFooter'
import ConfirmPopup from '../Popups/ConfirmPopup'
import {
  fetchInspectionTemplates,
  selectInspectionTemplates,
  bulkDeleteInspectionTemplates,
} from '../../reducers/inspectionsReducer'
import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import API from '../../api/api'

import { templatesColumns } from './helpers'
import InternalInspectionsToolbar from './InternalInspectionsToolbar'

const InternalInspectionsTemplates = ({
  building,
  templates,
  fetchInspectionTemplates = () => {},
  bulkDeleteInspectionTemplates = () => {},
}) => {
  const { t } = useTranslation(['internal-inspections', 'common'])
  const [isLoading, setSiteLoading] = useState(false)
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [selectedRowsIds, setSelectedRowsIds] = useState([])
  const [clearSelectedRows, setClearSelectedRows] = useState(() => () => {})
  const [search, setSearch] = useState('')
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [isBulkDeleteLoading, setIsBulkDeleteLoading] = useState(false)
  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false)
  const [isDeleteInspectionPopupOpen, setIsDeleteInspectionPopupOpen] = useState(false)
  const [isDuplicateInspectionPopupOpen, setIsDuplicateInspectionPopupOpen] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const navigate = useNavigate()

  const handleSetPageChange = (pageNum) => {
    setPageNumber(pageNum)
  }

  const onDeleteInspection = async () => {
    setIsBulkDeleteLoading(true)

    try {
      await bulkDeleteInspectionTemplates(selectedRowsIds)
      setIsDeleteInspectionPopupOpen(false)
      clearSelectedRows()
      fetchTemplates()
      displaySnackbar(t('internal-inspections:templates.deletedSuccessfully'))
    } catch {
      displaySnackbar(t('internal-inspections:templates.errorDeleting'), true)
    }

    setIsBulkDeleteLoading(false)
  }

  const onRowSelect = useCallback((ids) => setSelectedRowsIds(ids), [])

  const memoizedColumns = useMemo(
    () => templatesColumns({ timezone: building ? building.timezone : '' }, t),
    [building, t],
  )

  const fetchTemplates = async (query) => {
    setSiteLoading(true)

    // Only reset to page 1 if we have an actual search query that's different from current search
    const isNewSearch = query !== undefined && query !== search

    const requestData = {
      buildingId: building.id,
      page: isNewSearch ? 1 : pageNumber,
      limit: showOnPage,
      search: query !== undefined ? query : search,
    }

    await fetchInspectionTemplates(requestData)
      .then((count) => setPaginationItemsCount(count))
      .catch(({ message }) => displaySnackbar(message, true))

    setSiteLoading(false)
  }

  const openEdit = (id) =>
    navigate(`/dashboard/building-dashboard/internal-inspections/template/edit/${id}`)

  const onRowClick = ({ original }) => {
    if (original.id) {
      openEdit(original.id)
    }
  }

  const onEdit = () => {
    if (selectedRowsIds.length === 1) {
      openEdit(selectedRowsIds[0])
    }
  }

  const onDuplicate = async () => {
    setIsDuplicateLoading(true)

    try {
      await API.duplicateInspectionTemplates(selectedRowsIds)
      await fetchTemplates()
      setIsDuplicateInspectionPopupOpen(false)
      clearSelectedRows()
      displaySnackbar(t('internal-inspections:templates.duplicatedSuccessfully'))
    } catch {
      displaySnackbar(t('internal-inspections:templates.errorDuplicating'), true)
    }

    setIsDuplicateLoading(false)
  }

  const debouncedFetch = debounce(fetchTemplates, 800)

  useEffect(() => {
    if (building) {
      fetchTemplates()
    }
  }, [building, pageNumber, showOnPage])

  useEffect(() => {
    if (prevSearchValue !== search) {
      window.addEventListener('keydown', debouncedFetch(search))
    }
    setPrevSearchValue(search)
    return () => {
      window.removeEventListener('keydown', debouncedFetch)
    }
  }, [search])

  return (
    <React.Fragment>
      <div className={`ctp-internal-inspections__container ${isLoading ? 'is-loading' : ''}`}>
        <InternalInspectionsToolbar
          onEdit={onEdit}
          onDelete={() => setIsDeleteInspectionPopupOpen(true)}
          onDuplicate={() => setIsDuplicateInspectionPopupOpen(true)}
          search={search}
          onSearch={(e) => setSearch(e.target.value)}
          isLoading={isLoading}
          selectedRows={selectedRowsIds}
        />
        <div className="internal-inspections-list-table">
          {templates.length ? (
            <FeaturedTable
              data={templates}
              columns={memoizedColumns}
              onChangeSelectedRows={onRowSelect}
              onRowClick={onRowClick}
              setClearSelectedRows={setClearSelectedRows}
            />
          ) : !isLoading ? (
            <div className="no-results">
              {t('internal-inspections:dashboard.noInspectionsFound')}
            </div>
          ) : null}
        </div>
        {templates.length ? (
          <PaginationFooter
            showOnPage={showOnPage}
            pageNumber={pageNumber}
            paginationItemsCount={paginationItemsCount}
            setSiteLoading={setSiteLoading}
            setShowOnPage={setShowOnPage}
            setPageNumber={handleSetPageChange}
          />
        ) : null}
      </div>
      <ConfirmPopup
        isOpen={isDeleteInspectionPopupOpen}
        isLoading={isBulkDeleteLoading}
        cancelCallback={() => setIsDeleteInspectionPopupOpen(false)}
        confirmCallback={onDeleteInspection}
        cancelText={t('common:actions.cancel')}
        confirmText={t('internal-inspections:templates.confirmDeleteTemplates')}
        title={t('internal-inspections:dashboard.confirmationTitle')}
        body={t('internal-inspections:templates.confirmDeleteBody')}
      />
      <ConfirmPopup
        isOpen={isDuplicateInspectionPopupOpen}
        isLoading={isDuplicateLoading}
        cancelCallback={() => setIsDuplicateInspectionPopupOpen(false)}
        confirmCallback={() => !isDuplicateLoading && onDuplicate()}
        className="is-caution"
        cancelText={t('common:actions.cancel')}
        confirmText={t('internal-inspections:templates.confirmDuplicateTemplates')}
        title={t('internal-inspections:dashboard.confirmationTitle')}
        body={t('internal-inspections:templates.confirmDuplicateBody')}
      />
    </React.Fragment>
  )
}

InternalInspectionsTemplates.propTypes = {
  building: PropTypes.object,
  templates: PropTypes.array,
  fetchInspectionTemplates: PropTypes.func,
  bulkDeleteInspectionTemplates: PropTypes.func,
}

export default connect(
  (state) => ({
    templates: selectInspectionTemplates(state),
    building: selectActiveBuilding(state),
  }),
  { fetchInspectionTemplates, bulkDeleteInspectionTemplates },
)(InternalInspectionsTemplates)
